// src/components/common/PickerShell.tsx
// 所有 Picker 的共用 Shell：backdrop、card、portal、header、swipe to dismiss

import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft } from 'lucide-react';
import useSwipeToDismiss from '../../hooks/useSwipeToDismiss';

interface PickerShellProps {
  onClose: () => void;
  children: React.ReactNode;
  /** 有傳才顯示 Back 按鈕（多步驟流程第二步，如時間選擇器） */
  onBack?: () => void;
}

/**
 * 所有 Picker 的共用 Shell
 *
 * Header 規則：
 * - 預設不顯示 header（單步驟 picker，觸發點 label 已提供語意）
 * - 有 onBack 時才顯示 header（多步驟流程第二步，如日期→時間）
 *
 * 關閉方式：backdrop 點擊 + swipe down to dismiss（行動版）
 */
const PickerShell: React.FC<PickerShellProps> = ({ onClose, children, onBack }) => {
  const { sheetRef, backdropRef } = useSwipeToDismiss({ onDismiss: onClose });

  const content = (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-modal-nested flex items-end md:items-center md:justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        className="bg-white w-full md:w-[360px] rounded-t-xl md:rounded-xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header：只有多步驟流程（有 onBack）才顯示 */}
        {onBack && (
          <div className="flex items-center px-4 py-3 border-b border-gray-100">
            <button
              onClick={onBack}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        )}

        {/* 內容 */}
        {children}

        {/* Safe Area for Mobile */}
        <div className="h-safe-area-inset-bottom md:hidden" />
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default PickerShell;
